import { useWallet } from '@solana/wallet-adapter-react';
import type { Dapp } from '@src/dto';
import type { Release } from '@src/dto/Release';
import { isRight } from 'fp-ts/Either';
import { Plus, Smartphone } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useDappDataOptional } from '../../contexts/DappDataContext';
import { useAsyncData } from '../../hooks/useAsyncData';
import { AuthStateType, useAuth } from '../../hooks/useAuth';
import { useDappPublish } from '../../hooks/useDappPublish';
import { useDappReleases } from '../../hooks/useDappReleases';
import { useDappStoreStatus } from '../../hooks/useDappStoreStatus';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePublisher } from '../../hooks/usePublisher';
import { useTRPC } from '../../hooks/useTRPC';
import {
  getDappNewVersionPath,
  getDappReleasePath,
} from '../../utils/dappRoutes';
import { captureAndReportError } from '../../utils/errorReporting';
import { getNewVersionBlockedMessage } from '../../utils/publicationAccess';
import { PUBLISHER_FROZEN_ACTION_TOOLTIP } from '../../utils/publisherFreeze';
import { getPublisherVerificationGate } from '../../utils/publisherVerification';
import { hasSuperadminAccess } from '../../utils/roleUtils';
import { Button } from '../Button';
import { useOptionalDappContext } from '../DappLayout';
import { PortalCard, PortalPage, PortalPageHeader } from '../portal';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { DashboardReleasesTable } from './DashboardReleasesTable';
import { DeleteReleaseDialog } from './DeleteReleaseDialog';

/** Releases page (Figma "home, multiple versions" 13327:27371). */
export const DashboardReleases = () => {
  const dappRoute = useOptionalDappContext();
  const dappId = dappRoute?.dappId;
  const dappRouteDapp = dappId
    ? { id: dappId, packageName: dappRoute.packageName }
    : null;
  const navigate = useNavigate();
  const trpc = useTRPC();
  const wallet = useWallet();
  const [authState] = useAuth();
  const { activePublisher } = usePublisher();
  const { dappData } = useDappDataOptional();
  const {
    releases,
    setReleases,
    isLoading: releasesLoading,
    error: releasesError,
    refetch: refetchReleases,
  } = useDappReleases(dappId);
  const { data: dapp, run: runDapp } = useAsyncData<Dapp | null>({
    initialData: null,
  });
  const storeStatus = useDappStoreStatus(dappId);
  const { isPublishing, handlePublish } = useDappPublish(dappId ?? null, () => {
    void refetchReleases();
    storeStatus.refetch();
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [releasePendingDelete, setReleasePendingDelete] =
    useState<Release | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeletingRelease, setIsDeletingRelease] = useState(false);

  const isSuperadmin =
    authState.type === AuthStateType.Authenticated &&
    hasSuperadminAccess(authState.user.role, authState.user.email);

  usePageTitle(
    dappData?.dappName
      ? `${dappData.dappName} - Releases | Publishing Portal`
      : 'Releases | Publishing Portal',
  );

  const loadDapp = useCallback(() => {
    if (!dappId) return;
    void runDapp(() => trpc.getDapp.query({ dappId }), {
      onSuccess: (result, helpers) => {
        if (isRight(result)) helpers.setData(result.right);
      },
      onError: (err) => {
        captureAndReportError('Failed to fetch dapp details:', err);
      },
    });
  }, [dappId, runDapp, trpc]);

  useEffect(() => {
    if (!dappId || authState.type !== AuthStateType.Authenticated) return;
    const timer = setTimeout(loadDapp, 100);
    return () => clearTimeout(timer);
  }, [dappId, authState.type, loadDapp]);

  useEffect(() => {
    setCurrentPage(1);
  }, [releases.length]);

  const isPublisherFrozen =
    activePublisher?.isFrozen === true || dapp?.publisherFrozen === true;
  const publisherVerification = getPublisherVerificationGate({
    status: activePublisher?.kybStatus,
    publisherType: activePublisher?.publisherType,
    kycKybBypassEnabled: activePublisher?.kycKybBypassEnabled,
  });

  /** Reason the New Version button is disabled, or null when it is enabled. */
  const getNewVersionBlockReason = (): string | null => {
    if (isPublisherFrozen) return PUBLISHER_FROZEN_ACTION_TOOLTIP;
    if (!publisherVerification.isApproved) {
      return publisherVerification.newVersionMessage;
    }
    if (!wallet.connected) return 'Connect a wallet to create a new version';
    if (
      dapp?.walletAddress &&
      wallet.publicKey &&
      dapp.walletAddress !== wallet.publicKey.toBase58()
    ) {
      return `Connect the wallet that minted the App NFT (${dapp.walletAddress.slice(0, 4)}...${dapp.walletAddress.slice(-4)})`;
    }
    return getNewVersionBlockedMessage(dapp) ?? null;
  };
  const newVersionBlockReason = getNewVersionBlockReason();

  const closeDeleteReleaseDialog = () => {
    if (isDeletingRelease) return;
    setReleasePendingDelete(null);
    setDeleteReason('');
  };

  const handleDeleteRelease = async () => {
    if (!releasePendingDelete || releasePendingDelete.source !== 'Portal') {
      return;
    }
    const releaseToDelete = releasePendingDelete;

    setIsDeletingRelease(true);
    try {
      const result = await trpc.release.deleteRelease.mutate({
        releaseId: releaseToDelete.id,
        reason: deleteReason.trim() || undefined,
      });

      if (result._tag === 'Left') {
        toast.error(result.left.message);
        return;
      }

      setReleases((prev) =>
        prev.filter((release) => release.id !== releaseToDelete.id),
      );
      setReleasePendingDelete(null);
      setDeleteReason('');
      toast.success('Release deleted successfully');
      loadDapp();
    } catch (error) {
      captureAndReportError(error, {
        operation: 'release.deleteRelease',
        component: 'DashboardReleases',
        dappId,
        releaseId: releaseToDelete.id,
      });
      toast.error('Failed to delete release');
    } finally {
      setIsDeletingRelease(false);
    }
  };

  const newVersionButton = (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex w-full sm:w-auto">
            <Button
              variant="primary"
              className="w-full min-w-[180px] disabled:pointer-events-none sm:w-auto"
              disabled={newVersionBlockReason !== null}
              onClick={() => {
                if (dappRouteDapp) {
                  navigate(getDappNewVersionPath(dappRouteDapp));
                }
              }}
            >
              <Plus className="size-4" />
              New Version
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{newVersionBlockReason ?? 'Create a new version of your app'}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const showEmptyState =
    !releasesLoading && !releasesError && releases.length === 0;

  return (
    <PortalPage>
      <PortalPageHeader
        icon={<Smartphone />}
        title="Releases"
        actions={newVersionButton}
      />

      {releasesError ? (
        <PortalCard className="p-8 text-center text-sm text-rose-400">
          {releasesError}
        </PortalCard>
      ) : showEmptyState ? (
        <PortalCard className="flex flex-col items-center gap-6 p-12 text-center">
          <Smartphone className="size-8 text-seeker-500" />
          <p className="text-sm text-seeker-400">
            No releases yet. Create your first release to get started.
          </p>
          {newVersionButton}
        </PortalCard>
      ) : (
        <DashboardReleasesTable
          releases={releases}
          currentPage={currentPage}
          isLoading={releasesLoading || !storeStatus.hasResolved}
          showActionsColumn={isSuperadmin}
          statusContext={{
            publishedVersionCode: dapp?.publishedVersionCode,
            storeStatus: storeStatus.storeStatus,
          }}
          canPublish={storeStatus.canPublish}
          isPublishing={isPublishing}
          onPublish={handlePublish}
          onPageChange={setCurrentPage}
          onOpenRelease={(release) => {
            if (release.source !== 'Hubspot' && dappRouteDapp) {
              navigate(getDappReleasePath(dappRouteDapp, release.id));
            }
          }}
          onRequestDeleteRelease={setReleasePendingDelete}
        />
      )}

      <DeleteReleaseDialog
        releasePendingDelete={releasePendingDelete}
        deleteReason={deleteReason}
        isDeletingRelease={isDeletingRelease}
        onClose={closeDeleteReleaseDialog}
        onDelete={handleDeleteRelease}
        onDeleteReasonChange={setDeleteReason}
      />
    </PortalPage>
  );
};
